import {
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { DeleteUserReq, SaveUserReq } from '../../entity/api/routes/user';
import type { User } from '../../entity/api/user';
import { createUser, deleteUser, getAuthUser, getUserByIds, updateUser } from '../../sdk/user';

// ============ QUERY KEYS ============

export const usersKeys = {
  all: ['users'] as const,
  authUser: function () {
    return [...usersKeys.all, 'auth'] as const;
  },
  byId: function (id: string) {
    return [...usersKeys.all, 'byId', id] as const;
  },
  byIds: function (ids: string[]) {
    return [...usersKeys.all, 'byIds', ids.sort().join(',')] as const;
  },
};

// ============ QUERIES ============

/**
 * Fetch the authenticated user
 * Replaces: getStores().users((s) => s.getAuthUserFromStore())
 */
export function useAuthUser(): UseQueryResult<User, Error> {
  return useQuery<User>({
    queryKey: usersKeys.authUser(),
    queryFn: async function () {
      const response = await getAuthUser();
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: false, // Don't retry auth failures
  });
}

/**
 * Fetch users by their IDs.
 * Checks individual byId cache entries first — if all users are already cached,
 * assembles the result without a network call. Otherwise fetches from the API
 * and caches each user individually.
 *
 * Replaces: getStores().users.getState().getByIds(ids)
 */
export function useUsersByIds(ids: string[]): UseQueryResult<User[], Error> {
  const queryClient = useQueryClient();

  return useQuery<User[]>({
    queryKey: usersKeys.byIds(ids),
    queryFn: async function () {
      if (ids.length === 0) {
        return [];
      }

      // Check how many ids are already cached individually
      const missing = ids.filter(function (id) {
        return queryClient.getQueryData(usersKeys.byId(id)) == null;
      });

      if (missing.length === 0) {
        // All users already cached individually — assemble from cache, no network call
        return ids.map(function (id) {
          return queryClient.getQueryData<User>(usersKeys.byId(id))!;
        });
      }

      const response = await getUserByIds(ids);

      for (const user of response.data) {
        if (user._id != null) {
          queryClient.setQueryData(usersKeys.byId(user._id), user);
        }
      }

      return response.data;
    },
    enabled: ids.length > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ============ MUTATIONS ============

/**
 * Create a new user
 * Replaces: getStores().users.getState().create(authToken)
 */
export function useCreateUser<
  T extends {
    authToken: Parameters<typeof createUser>[0];
    utmData: Parameters<typeof createUser>[1];
  },
>(): UseMutationResult<User, Error, T> {
  const queryClient = useQueryClient();

  return useMutation<User, Error, T>({
    mutationFn: async (data) => {
      const response = await createUser(data.authToken, data.utmData);
      return response.data;
    },
    onSuccess: (data) => {
      // Set the auth user in cache
      queryClient.setQueryData(usersKeys.authUser(), data);
    },
  });
}

/**
 * Update the current user
 * Replaces: getStores().users.getState().update(data)
 */
export function useUpdateUser(): UseMutationResult<User | undefined, Error, SaveUserReq['body']> {
  const queryClient = useQueryClient();

  return useMutation<User | undefined, Error, SaveUserReq['body']>({
    mutationFn: async (data: SaveUserReq['body']) => {
      // UTM values should only be set on initial signup, not updated afterwards
      const response = await updateUser(data);
      return response?.data;
    },
    onSuccess: function (data) {
      if (data != null) {
        // Update the auth user cache
        queryClient.setQueryData(usersKeys.authUser(), data);
      }
    },
  });
}

/**
 * Delete the current user
 * Replaces: getStores().users.getState().delete(data)
 */
export function useDeleteUser(): UseMutationResult<void, Error, DeleteUserReq['body']> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteUserReq['body']>({
    mutationFn: async (data: DeleteUserReq['body']) => {
      await deleteUser(data);
    },
    onSuccess: function () {
      // Clear the auth user from cache
      queryClient.removeQueries({ queryKey: usersKeys.authUser() });
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get the authenticated user from cache (imperative access)
 * Use this when you need to access the user outside of a React component
 *
 * @example
 * const user = getAuthUserFromCache(queryClient);
 */
export function getAuthUserFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): User | undefined {
  return queryClient.getQueryData<User>(usersKeys.authUser());
}

/**
 * Get a user by ID from cache (imperative access)
 */
export function getUserByIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): User | undefined {
  return queryClient.getQueryData<User>(usersKeys.byId(id));
}

/**
 * Set a user in cache by ID (for cross-store synchronization)
 * Also updates the auth user cache if the user matches the authenticated user
 */
export function setUserInCache(queryClient: ReturnType<typeof useQueryClient>, user: User): void {
  if (user._id != null) {
    // Update the byId cache
    queryClient.setQueryData(usersKeys.byId(user._id), user);

    // Also update auth user cache if this is the authenticated user
    const authUser = queryClient.getQueryData<User>(usersKeys.authUser());
    if (authUser != null && authUser._id === user._id) {
      queryClient.setQueryData(usersKeys.authUser(), user);
    }
  }
}

/**
 * Set multiple users in cache (for cross-store synchronization)
 */
export function setUsersInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  users: User[]
): void {
  for (const user of users) {
    setUserInCache(queryClient, user);
  }
}

/**
 * Remove user from cache (for socket delete events)
 * Also removes from auth user cache if this is the authenticated user
 */
export function removeUserFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string
): void {
  // Remove from individual user cache
  queryClient.removeQueries({ queryKey: usersKeys.byId(userId) });

  // Also check if this is the auth user (edge case: user deleted themselves)
  const authUser = queryClient.getQueryData<User>(usersKeys.authUser());
  if (authUser != null && authUser._id === userId) {
    queryClient.removeQueries({ queryKey: usersKeys.authUser() });
  }

  // Also update the byIds cache if it exists - remove the user from any cached byIds queries
  queryClient.setQueriesData<User[]>({ queryKey: usersKeys.all }, function (oldData) {
    if (oldData == null || !Array.isArray(oldData)) {
      return oldData;
    }
    return oldData.filter(function (user) {
      return user._id !== userId;
    });
  });
}

/**
 * Invalidate the auth user query to force a refetch
 */
export function invalidateAuthUser(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: usersKeys.authUser() });
}

/**
 * Prefetch the auth user (useful for initial app load)
 */
export async function prefetchAuthUser(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: usersKeys.authUser(),
    queryFn: async function () {
      const response = await getAuthUser();
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch auth user and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic
 */
export async function fetchAuthUserImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<User | undefined> {
  const user = await queryClient.fetchQuery({
    queryKey: usersKeys.authUser(),
    queryFn: async function () {
      const response = await getAuthUser();
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  return user;
}

/**
 * Create user imperatively (for use outside React components like signup flow)
 * This creates a new user and caches it
 */
export async function createUserImperative(
  queryClient: QueryClient,
  authToken: string,
  utmData: {
    signupSource: string;
    signupMedium: string;
    signupCampaign: string;
    signupTerm: string;
    signupGclid?: string;
    signupFbclid?: string;
    signupLiFatId?: string;
  }
): Promise<{ user: User; created: boolean }> {
  const response = await createUser(authToken, utmData);
  const user = response.data;

  // Cache the new user
  queryClient.setQueryData(usersKeys.authUser(), user);

  return { user, created: response.created };
}

/**
 * Update user imperatively (for use outside React components like utility functions)
 */
export async function updateUserImperative(
  queryClient: QueryClient,
  data: SaveUserReq['body']
): Promise<User | undefined> {
  // UTM values should only be set on initial signup, not updated afterwards
  const response = await updateUser(data);
  const user = response?.data;

  if (user != null) {
    // Update the auth user cache
    queryClient.setQueryData(usersKeys.authUser(), user);
  }

  return user;
}

/**
 * Delete user imperatively (for use outside React components)
 */
export async function deleteUserImperative(
  queryClient: QueryClient,
  data: DeleteUserReq['body']
): Promise<void> {
  await deleteUser(data);

  // Clear the auth user from cache
  queryClient.removeQueries({ queryKey: usersKeys.authUser() });
}

/**
 * Prefetch users by their IDs and cache them (imperative)
 * Used by articles/products queries to prefetch "created by" user data
 *
 * Uses TanStack Query's prefetchQuery to enable request deduplication.
 * Multiple simultaneous calls with the same user IDs will be deduplicated.
 */
export async function prefetchUsersByIds(queryClient: QueryClient, ids: string[]): Promise<void> {
  if (ids.length === 0) {
    return;
  }

  // Deduplicate IDs
  const uniqueIds = [...new Set(ids)].filter(Boolean);
  if (uniqueIds.length === 0) {
    return;
  }

  // Use prefetchQuery instead of direct API call to enable request deduplication
  // Multiple simultaneous calls with the same user IDs will share a single API request
  await queryClient.prefetchQuery({
    queryKey: usersKeys.byIds(uniqueIds),
    queryFn: async function () {
      const response = await getUserByIds(uniqueIds);

      // Also cache each user individually for cross-query access
      // This mirrors the behavior in useUsersByIds hook
      for (const user of response.data) {
        if (user._id != null) {
          queryClient.setQueryData(usersKeys.byId(user._id), user);
        }
      }

      return response.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes - matches useUsersByIds hook
  });
}
